import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { ComponentPreview } from '@client/components/_ui/display/ComponentPreview';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { message } from '@client/components/_ui/feedback/message';
import { complianceAuditTemplateFormStore } from '@client/components/compliance-audit-templates/store';
import { TemplateTabSearch } from '@client/components/compliance-audit-templates/TemplateTabSearch';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { type RouterOutputs, trpc } from '@client/trpc/client';
import { createFileRoute, Outlet, useSearch } from '@tanstack/react-router';
import { Tooltip } from 'antd';
import { useMemo, useState } from 'react';

export const Route = createFileRoute(
    '/admin-console/platform-content/compliance-audit-templates/details/$complianceAuditTemplateId/evidence-types',
)({
    staticData: {
        ui: { useParentTitle: false },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <EvidenceTypes />
        </PageController>
    );
}

type EvidenceTypeRow =
    RouterOutputs['admin']['complianceAuditTemplate']['listComplianceAuditTemplateEvidenceTypes']['rows'][number];

const columnHelper = createColumnHelper<EvidenceTypeRow>();

/**
 * CUR-424 — working version's evidence types (products-v2 contact-types donor).
 * Unlike the donor, edits on a PUBLISHED working version are allowed — the
 * server auto-clones a new DRAFT (design D4) — so the tab is only read-only for
 * an archived template. The delete guard is server-authoritative; the disabled
 * button mirrors it as UX.
 */
function EvidenceTypes() {
    const { complianceAuditTemplateId } = Route.useParams();
    // `version` = read-only snapshot viewing, validated by the details route.
    const { version } = useSearch({
        from: '/admin-console/platform-content/compliance-audit-templates/details/$complianceAuditTemplateId',
    });
    const isReadOnly = complianceAuditTemplateFormStore.use.isReadonly();
    const { trpcUtils } = useTrpc();
    const navigate = useNavigate();

    const { data, isPending, error, refetch } =
        trpc.admin.complianceAuditTemplate.listComplianceAuditTemplateEvidenceTypes.useQuery({
            complianceAuditTemplateId,
            version,
        });

    const deleteMutation = trpc.admin.complianceAuditTemplate.deleteComplianceAuditTemplateEvidenceType.useMutation();

    const [search, setSearch] = useState('');
    const filteredRows = useMemo(() => {
        const query = search.trim().toLowerCase();
        return (data?.rows ?? []).filter(
            (row) =>
                !query ||
                row.name.toLowerCase().includes(query) ||
                (row.description ?? '').toLowerCase().includes(query) ||
                (row.recognitionHint ?? '').toLowerCase().includes(query),
        );
    }, [data?.rows, search]);

    const tableProps = usePrismaTable({
        rowKey: 'id',
        data: filteredRows,
        total: filteredRows.length,
        loading: isPending,
        queryParams: {},
        onParamsChange() {},
        pagination: { position: 'none' },
        columns: [
            columnHelper.accessor('name', {
                id: 'name',
                header: 'Label',
            }),
            columnHelper.accessor('description', {
                id: 'description',
                header: 'What it is',
                className: 'max-w-xs',
                cell: ({ row }) => <ComponentPreview>{row.description || '-'}</ComponentPreview>,
            }),
            columnHelper.accessor('recognitionHint', {
                id: 'recognitionHint',
                // CUR-168 made evidence types reference-only — retrieval runs off
                // each sub-step's retrieval query, so the hint no longer steers
                // the AI and the old "How the AI recognises it" header was false.
                header: 'Recognition hint',
                className: 'max-w-xs',
                cell: ({ row }) => <ComponentPreview>{row.recognitionHint || '-'}</ComponentPreview>,
            }),
            columnHelper.display({
                id: 'usedBy',
                header: 'Used by',
                cell: ({ row }) =>
                    row.usedBySubSteps.length ? (
                        <Tooltip title={row.usedBySubSteps.map(({ title }) => title).join(', ')}>
                            <span>
                                {row.usedBySubSteps.length} sub-step{row.usedBySubSteps.length === 1 ? '' : 's'}
                            </span>
                        </Tooltip>
                    ) : (
                        <span className="text-subtle-foreground">Unused</span>
                    ),
                width: 120,
            }),
            ...(isReadOnly
                ? []
                : [
                      columnHelper.actions({
                          id: 'actions',
                          cell: ({ row }) => (
                              <Button.Delete
                                  disabled={row.usedBySubSteps.length > 0}
                                  tooltip={
                                      row.usedBySubSteps.length
                                          ? 'Used by one or more sub-steps — remove those references first'
                                          : 'Delete evidence type'
                                  }
                                  onConfirm={(onSuccess) =>
                                      deleteMutation.mutate(
                                          {
                                              complianceAuditTemplateId,
                                              selectedVersion: version,
                                              complianceAuditTemplateEvidenceTypeId: row.id,
                                          },
                                          {
                                              onSuccess(result) {
                                                  onSuccess();
                                                  if (result.createdNewDraft) {
                                                      message.info(
                                                          'A new draft version was created from the published version',
                                                      );
                                                  }
                                                  void trpcUtils.admin.complianceAuditTemplate.invalidate();
                                              },
                                              onError(error) {
                                                  message.destroy();
                                                  errorMessage.show(error);
                                              },
                                          },
                                      )
                                  }
                              />
                          ),
                      }),
                  ]),
        ],
    });

    if (error) {
        return <InlineQueryError error={error} onRetry={() => void refetch()} title="Couldn't load evidence types" />;
    }

    return (
        <>
            <Outlet />
            <TableToolbar
                filters={
                    <TemplateTabSearch
                        storageKey="compliance-audit-template-evidence-types-search"
                        placeholder="Search by label, description or hint..."
                        onChange={setSearch}
                    />
                }
                actions={
                    !isReadOnly && (
                        <Button.Add.Link
                            to="/admin-console/platform-content/compliance-audit-templates/details/$complianceAuditTemplateId/evidence-types/add"
                            params={{ complianceAuditTemplateId }}
                            variant="primary"
                            label="New evidence type"
                        />
                    )
                }
            />
            <PrismaTable
                {...tableProps}
                onRow={({ id }) => ({
                    className: 'cursor-pointer',
                    onClick(e) {
                        navigate(
                            {
                                to: '/admin-console/platform-content/compliance-audit-templates/details/$complianceAuditTemplateId/evidence-types/edit/$complianceAuditTemplateEvidenceTypeId',
                                params: {
                                    complianceAuditTemplateId,
                                    complianceAuditTemplateEvidenceTypeId: id,
                                },
                            },
                            e,
                        );
                    },
                })}
            />
        </>
    );
}
